import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";
import toast from "react-hot-toast";

import { cn } from "@/lib/utils";

import type { ChangeEvent } from "react";

interface PlaceData {
  cityCountyNeighborhood: string;
  state: string;
  latitude: number | null;
  longitude: number | null;
  formattedAddress: string;
  placeId?: string;
  // parsed address components (available when types includes "address")
  streetNumber?: string;
  streetName?: string;
  streetAddress?: string; // streetNumber + streetName combined
  subpremise?: string; // apt, suite, unit, etc. (from Google's subpremise component)
  city?: string;
  zip?: string;
}

interface GoogleAutocompleteProps {
  onPlaceChanged?: (_: PlaceData) => void;
  onChange?: (_: string) => void;
  placeholder?: string;
  inputClassName?: string;
  disabled?: boolean;
  shouldClear?: boolean;
  value?: string;
  /** Google Places types - defaults to ["geocode"] for cities/regions, use ["address"] for street addresses */
  types?: string[];
  /** When true, overrides Google's formatted_address with a full mask built from address components:
   *  "{name}, {city}, {state} - {country}" (deduped). Defaults to false. */
  fullAddressMask?: boolean;
}

type Libraries = Array<"places">;

const GoogleAutocomplete = forwardRef<HTMLInputElement, GoogleAutocompleteProps>(
  (
    {
      onPlaceChanged = () => {},
      onChange,
      placeholder = "Search...",
      inputClassName = "",
      disabled = false,
      shouldClear = false,
      value,
      types = ["geocode"],
      fullAddressMask = false,
    },
    ref
  ) => {
    // eslint-disable-next-line no-undef
    const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);

    // stupid way of handling this but prevents console warnings about performance issues
    // defining `libraries` outside the component body wasn't actually working
    // https://github.com/JustFly1984/react-google-maps-api/issues/238#issuecomment-761076533
    const [libraries] = useState<Libraries>(["places"]);
    const inputRef = useRef<HTMLInputElement>(null);

    // Forward the inputRef to the parent component
    useImperativeHandle(ref, () => inputRef.current!);

    const { isLoaded, loadError: scriptLoadError } = useJsApiLoader({
      id: "google-map-script",
      googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY!,
      libraries,
    });

    // this is a hack to get the value from the parent component to update
    // the google autocomplete input

    useEffect(() => {
      if (inputRef.current && value !== undefined) {
        inputRef.current.value = value;
      }
    }, [value, isLoaded]);

    // shared classes so loading/error/loaded states all occupy the same box -> no layout shift
    const inputClasses = cn(
      "max-w-full px-3 py-2 border rounded-md text-base mb-6 w-full focus-visible:outline-hidden focus-visible:ring-1 ring-ash-500",
      inputClassName
    );

    if (scriptLoadError) {
      return (
        <input
          type="search"
          placeholder="Location search temporarily unavailable"
          disabled
          className={cn(inputClasses, "text-ash-400 placeholder:text-ash-400")}
        />
      );
    }

    // pre-load placeholder reserves the same slot the autocomplete will occupy
    if (!isLoaded) {
      return (
        <input
          type="search"
          placeholder={placeholder}
          disabled
          className={cn(inputClasses, "text-ash-800 placeholder:text-ash-500")}
        />
      );
    }

    const handlePlaceChanged = async (): Promise<void> => {
      if (!autocomplete || !autocomplete.getPlace) return;

      const place = autocomplete.getPlace();

      if (!place?.geometry) {
        // User entered the name of a Place that was not suggested and
        // pressed the Enter key, or the Place Details request failed
        toast("Please select a location from the dropdown", {
          icon: "🌆",
        });
        return;
      }

      if (place?.address_components) {
        const getComponent = (type: string, useShortName = true) => {
          const component = place.address_components?.find((c) => c.types?.includes(type));
          return useShortName ? component?.short_name || "" : component?.long_name || "";
        };

        const state = getComponent("administrative_area_level_1");
        const cityCountyNeighborhood = place?.name || "";

        const streetNumber = getComponent("street_number", false);
        const streetName = getComponent("route", false);
        const streetAddress = [streetNumber, streetName].filter(Boolean).join(" ");
        const subpremise = getComponent("subpremise", false);
        const city = getComponent("locality", false) || getComponent("sublocality", false);
        const zip = getComponent("postal_code", false);

        const latitude = place?.geometry?.location?.lat() || null;
        const longitude = place?.geometry?.location?.lng() || null;
        const placeId = place?.place_id || "";

        const isAddressType = types.includes("address");
        const hasValidLocation = isAddressType ? !!streetAddress : !!(cityCountyNeighborhood && state);

        let formattedAddress = place?.formatted_address || "";

        if (fullAddressMask) {
          const stateLong = getComponent("administrative_area_level_1", false);
          const country = getComponent("country", false);
          const locationParts = [cityCountyNeighborhood, city, stateLong].filter(
            (part, index, arr) => !!part && arr.indexOf(part) === index
          );
          formattedAddress = country ? `${locationParts.join(", ")} - ${country}` : locationParts.join(", ");

          // Safe to mutate directly: input is uncontrolled (defaultValue, not value)
          // and Google's Autocomplete already owns the DOM value.
          if (inputRef.current) {
            inputRef.current.value = formattedAddress;
          }
        }

        if (hasValidLocation) {
          onPlaceChanged({
            cityCountyNeighborhood,
            state,
            latitude,
            longitude,
            formattedAddress,
            placeId,
            streetNumber,
            streetName,
            streetAddress,
            subpremise,
            city,
            zip,
          });

          if (inputRef.current && shouldClear && !fullAddressMask) {
            inputRef.current.value = "";
          }
        } else {
          const message = isAddressType
            ? "Please select a valid street address"
            : "Please select a city, county, or neighborhood";
          toast(message, { icon: "🌆" });

          if (inputRef.current) {
            inputRef.current.value = "";
          }
        }
      }
    };

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
      onChange?.(event.target.value);

      if (event.target.value === "") {
        // If the input is cleared, call onPlaceChanged with empty values
        onPlaceChanged({
          cityCountyNeighborhood: "",
          state: "",
          latitude: null,
          longitude: null,
          formattedAddress: "",
          streetNumber: "",
          streetName: "",
          streetAddress: "",
          city: "",
          zip: "",
        });
      }
    };

    // eslint-disable-next-line no-undef
    const handleAutocompleteLoad = (autocompleteInstance: google.maps.places.Autocomplete): void => {
      autocompleteInstance.setFields(["address_components", "geometry", "formatted_address", "name", "place_id"]);
      setAutocomplete(autocompleteInstance);
    };

    return (
      <Autocomplete
        onLoad={handleAutocompleteLoad}
        onPlaceChanged={handlePlaceChanged}
        options={{
          componentRestrictions: { country: ["us", "ca"] },
          types,
        }}
      >
        <input
          disabled={disabled}
          ref={inputRef}
          defaultValue={value}
          onChange={handleInputChange}
          onKeyDown={(e) => {
            // prevents enter from submitting any parent form
            if (e.key === "Enter") {
              e.preventDefault();
            }
          }}
          type="search"
          placeholder={placeholder}
          className={cn(inputClasses, "text-ash-800 placeholder:text-ash-500")}
        />
      </Autocomplete>
    );
  }
);

GoogleAutocomplete.displayName = "GoogleAutocomplete";

export default GoogleAutocomplete;
